import React, { useState, useEffect } from 'react';
import { Modal, Button, Tabs, Tab, Spinner, Table, Row, Col, Badge } from 'react-bootstrap';
import { FaFileExcel, FaChevronRight, FaChevronDown } from 'react-icons/fa';
import moment from 'moment';
import { toast } from 'react-toastify';
import {
  obterContagensEventoRsvp,
  listarConfirmadosEventoRsvp,
  obterDetalhesConfirmacaoEventoRsvp,
  listarPendentesEventoRsvp,
  listarComparecimentoEventoRsvp,
  exportarConfirmadosEventoRsvpExcel,
  exportarPendentesEventoRsvpExcel,
  exportarCompareceramEventoRsvpExcel,
  exportarNaoCompareceramEventoRsvpExcel,
} from '~/Services/Service';

const extrairMensagemErro = (err, fallback) => {
  if (err && err.message) return err.message;
  if (typeof err === 'string') return err;
  return fallback;
};

const formatarCpf = (cpf) => {
  if (!cpf) return '';
  const so = String(cpf).replace(/\D/g, '').padStart(11, '0');
  return `${so.substr(0, 3)}.${so.substr(3, 3)}.${so.substr(6, 3)}-${so.substr(9, 2)}`;
};

const baixarBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  window.URL.revokeObjectURL(url);
};

const LinhaConfirmado = ({ confirmado }) => {
  const [aberto, setAberto] = useState(false);
  const [detalhes, setDetalhes] = useState(null);
  const [carregando, setCarregando] = useState(false);

  const toggle = () => {
    const novoAberto = !aberto;
    setAberto(novoAberto);
    if (novoAberto && detalhes === null && !carregando) {
      setCarregando(true);
      obterDetalhesConfirmacaoEventoRsvp(confirmado.codigo_confirmacao)
        .then((res) => setDetalhes(res || { presencas: [], acompanhantes: [] }))
        .catch((err) => {
          toast.error(extrairMensagemErro(err, 'Erro ao carregar detalhes.'));
          setDetalhes({ presencas: [], acompanhantes: [] });
        })
        .finally(() => setCarregando(false));
    }
  };

  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={toggle}>
        <td className="text-center" style={{ width: 30 }}>
          {aberto ? <FaChevronDown /> : <FaChevronRight />}
        </td>
        <td>{confirmado.nome_cliente}</td>
        <td style={{ whiteSpace: 'nowrap' }}>{formatarCpf(confirmado.cpf_cliente)}</td>
        <td style={{ whiteSpace: 'nowrap' }}>{confirmado.telefone}</td>
        <td>{confirmado.quadras_lotes}</td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {confirmado.data_confirmacao
            ? moment(confirmado.data_confirmacao).format('DD/MM/YYYY HH:mm')
            : ''}
        </td>
        <td className="text-center">
          <Badge variant={confirmado.total_presentes > 0 ? 'success' : 'secondary'}>
            {confirmado.total_presentes}/{confirmado.total_esperado}
          </Badge>
        </td>
      </tr>
      {aberto && (
        <tr>
          <td colSpan={7} style={{ background: '#f8f9fa' }}>
            {carregando ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" /> Carregando detalhes...
              </div>
            ) : detalhes ? (
              <Row>
                <Col md={6}>
                  <strong>Presenças</strong>
                  {detalhes.presencas && detalhes.presencas.length > 0 ? (
                    <Table size="sm" className="mt-2 mb-0" borderless>
                      <thead>
                        <tr>
                          <th>Nome</th>
                          <th>CPF</th>
                          <th className="text-center">Vai?</th>
                          <th className="text-center">Compareceu?</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detalhes.presencas.map((p, idx) => (
                          <tr key={idx}>
                            <td>{p.nome_titular}</td>
                            <td>{formatarCpf(p.cpf_titular)}</td>
                            <td className="text-center">
                              {p.confirma_presenca ? (
                                <Badge variant="success">Sim</Badge>
                              ) : (
                                <Badge variant="secondary">Não</Badge>
                              )}
                            </td>
                            <td className="text-center">
                              {p.presenca_entrada ? (
                                <Badge variant="success">Sim</Badge>
                              ) : (
                                <Badge variant="secondary">Não</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <p className="text-muted small mt-2 mb-0">Sem presenças.</p>
                  )}
                </Col>
                <Col md={6}>
                  <strong>Acompanhantes</strong>
                  {detalhes.acompanhantes && detalhes.acompanhantes.length > 0 ? (
                    <Table size="sm" className="mt-2 mb-0" borderless>
                      <thead>
                        <tr>
                          <th>Nome</th>
                          <th>Telefone</th>
                          <th className="text-center">Idade</th>
                          <th className="text-center">Compareceu?</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detalhes.acompanhantes.map((a, idx) => (
                          <tr key={idx}>
                            <td>{a.nome}</td>
                            <td>{a.telefone}</td>
                            <td className="text-center">{a.idade}</td>
                            <td className="text-center">
                              {a.presenca_entrada ? (
                                <Badge variant="success">Sim</Badge>
                              ) : (
                                <Badge variant="secondary">Não</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <p className="text-muted small mt-2 mb-0">Nenhum acompanhante.</p>
                  )}
                </Col>
              </Row>
            ) : null}
          </td>
        </tr>
      )}
    </>
  );
};

// Tabela usada nas guias Compareceram / Não compareceram — uma linha por pessoa
// (titular/cônjuge de presença + cada acompanhante). Sem badge: cada linha já é
// um indivíduo. "1ª entrada" só aparece na guia Compareceram.
const TabelaComparecimento = ({ linhas, mostrarEntrada }) => (
  <Table striped hover size="sm" responsive style={{ fontSize: '0.85rem' }}>
    <thead>
      <tr>
        <th>Nome</th>
        <th>Tipo</th>
        <th style={{ whiteSpace: 'nowrap' }}>CPF</th>
        <th style={{ whiteSpace: 'nowrap' }}>Telefone</th>
        <th>Quadras/Lotes</th>
        {mostrarEntrada && (
          <th style={{ whiteSpace: 'nowrap' }}>Entrada</th>
        )}
      </tr>
    </thead>
    <tbody>
      {linhas.map((c) => (
        <tr key={`${c.origem}-${c.codigo_pessoa}`}>
          <td>{c.nome}</td>
          <td>{c.tipo}</td>
          <td style={{ whiteSpace: 'nowrap' }}>{c.cpf ? formatarCpf(c.cpf) : ''}</td>
          <td style={{ whiteSpace: 'nowrap' }}>{c.telefone}</td>
          <td>{c.quadras_lotes}</td>
          {mostrarEntrada && (
            <td style={{ whiteSpace: 'nowrap' }}>
              {c.data_entrada
                ? moment(c.data_entrada).format('DD/MM/YYYY HH:mm')
                : ''}
            </td>
          )}
        </tr>
      ))}
    </tbody>
  </Table>
);

const RelatorioEventoModal = ({ evento, onClose }) => {
  const [tab, setTab] = useState('confirmados');
  const [contagens, setContagens] = useState(null);
  const [confirmados, setConfirmados] = useState(null);
  const [pendentes, setPendentes] = useState(null);
  const [loadingPendentes, setLoadingPendentes] = useState(false);
  const [comparecimento, setComparecimento] = useState(null);
  const [loadingComparecimento, setLoadingComparecimento] = useState(false);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    if (!evento) return;
    obterContagensEventoRsvp(evento.codigo)
      .then((res) => setContagens(res))
      .catch(() => setContagens(null));
    listarConfirmadosEventoRsvp(evento.codigo)
      .then((res) => setConfirmados(res || []))
      .catch((err) => toast.error(extrairMensagemErro(err, 'Erro ao carregar confirmados.')));
  }, [evento]);

  const carregarPendentes = () => {
    if (pendentes !== null || loadingPendentes) return;
    setLoadingPendentes(true);
    listarPendentesEventoRsvp(evento.codigo)
      .then((res) => setPendentes(res || []))
      .catch((err) => {
        toast.error(extrairMensagemErro(err, 'Erro ao carregar pendentes.'));
        setPendentes([]);
      })
      .finally(() => setLoadingPendentes(false));
  };

  const carregarComparecimento = () => {
    if (comparecimento !== null || loadingComparecimento) return;
    setLoadingComparecimento(true);
    listarComparecimentoEventoRsvp(evento.codigo)
      .then((res) => setComparecimento(res || []))
      .catch((err) => {
        toast.error(extrairMensagemErro(err, 'Erro ao carregar comparecimento.'));
        setComparecimento([]);
      })
      .finally(() => setLoadingComparecimento(false));
  };

  const handleSelectTab = (key) => {
    setTab(key);
    if (key === 'pendentes') carregarPendentes();
    if (key === 'compareceram' || key === 'nao-compareceram') carregarComparecimento();
  };

  const compareceram = comparecimento
    ? comparecimento.filter((c) => c.presente)
    : null;
  const naoCompareceram = comparecimento
    ? comparecimento.filter((c) => !c.presente)
    : null;

  const exportar = (tipo) => {
    if (exportando) return;
    setExportando(true);

    const map = {
      confirmados: {
        fn: exportarConfirmadosEventoRsvpExcel,
        nome: `confirmados_${evento.codigo}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`,
      },
      pendentes: {
        fn: exportarPendentesEventoRsvpExcel,
        nome: `pendentes_${evento.codigo}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`,
      },
      compareceram: {
        fn: exportarCompareceramEventoRsvpExcel,
        nome: `compareceram_${evento.codigo}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`,
      },
      'nao-compareceram': {
        fn: exportarNaoCompareceramEventoRsvpExcel,
        nome: `nao_compareceram_${evento.codigo}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`,
      },
    };

    const cfg = map[tipo];
    cfg
      .fn(evento.codigo)
      .then((blob) => baixarBlob(blob, cfg.nome))
      .catch((err) => toast.error(extrairMensagemErro(err, 'Erro ao exportar Excel.')))
      .finally(() => setExportando(false));
  };

  if (!evento) return null;

  return (
    <Modal show onHide={onClose} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Relatório — {evento.nome_evento}
          {evento.ativo && (
            <Badge variant="success" className="ml-2">
              Ativo
            </Badge>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {contagens && (
          <div
            className="mb-3 p-3"
            style={{
              background: '#e7f5ff',
              border: '1px solid #74c0fc',
              borderRadius: 4,
              color: '#1864ab',
            }}
          >
            <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>
              {contagens.presencas_confirmadas + contagens.total_acompanhantes}{' '}
              pessoas vão ao evento
            </div>
            <small style={{ color: '#1971c2' }}>
              <strong>{contagens.presencas_confirmadas}</strong> titulares/cônjuges
              {' + '}
              <strong>{contagens.total_acompanhantes}</strong> acompanhantes
              {' · '}
              <strong>{contagens.total_confirmacoes}</strong> clientes confirmaram
            </small>
          </div>
        )}
        <Tabs activeKey={tab} onSelect={handleSelectTab} className="mb-3">
          <Tab eventKey="confirmados" title="Confirmados">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">
                Clique em uma linha para ver presenças (sim/não) e acompanhantes.
              </small>
              <Button
                size="sm"
                variant="outline-success"
                onClick={() => exportar('confirmados')}
                disabled={exportando || !confirmados || confirmados.length === 0}
              >
                <FaFileExcel className="mr-1" /> Exportar Excel
              </Button>
            </div>
            {confirmados === null ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" /> Carregando...
              </div>
            ) : confirmados.length === 0 ? (
              <p className="text-muted text-center py-4">
                Nenhuma confirmação registrada.
              </p>
            ) : (
              <Table
                striped
                hover
                size="sm"
                responsive
                style={{ fontSize: '0.85rem' }}
              >
                <thead>
                  <tr>
                    <th style={{ width: 30 }}></th>
                    <th>Cliente</th>
                    <th style={{ whiteSpace: 'nowrap' }}>CPF Cliente</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Telefone</th>
                    <th>Quadras/Lotes</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Confirmado em</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap' }}>
                      Presentes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {confirmados.map((c) => (
                    <LinhaConfirmado
                      key={c.codigo_confirmacao}
                      confirmado={c}
                    />
                  ))}
                </tbody>
              </Table>
            )}
          </Tab>

          <Tab eventKey="pendentes" title="Pendentes">
            <div className="d-flex justify-content-end align-items-center mb-2">
              <Button
                size="sm"
                variant="outline-success"
                onClick={() => exportar('pendentes')}
                disabled={exportando || !pendentes || pendentes.length === 0}
              >
                <FaFileExcel className="mr-1" /> Exportar Excel
              </Button>
            </div>
            {loadingPendentes ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" /> Carregando pendentes...
              </div>
            ) : pendentes === null ? (
              <p className="text-muted text-center py-4">
                Clique nesta aba para carregar.
              </p>
            ) : pendentes.length === 0 ? (
              <p className="text-muted text-center py-4">
                Nenhum elegível pendente — todos já confirmaram.
              </p>
            ) : (
              <Table
                striped
                hover
                size="sm"
                responsive
                style={{ fontSize: '0.85rem' }}
              >
                <thead>
                  <tr>
                    <th>Quadra</th>
                    <th>Lote</th>
                    <th>Nome</th>
                    <th>Tipo</th>
                    <th style={{ whiteSpace: 'nowrap' }}>CPF</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Telefone</th>
                  </tr>
                </thead>
                <tbody>
                  {pendentes.map((p, idx) => (
                    <tr key={idx}>
                      <td>{p.quadra}</td>
                      <td>{p.lote}</td>
                      <td>{p.nome}</td>
                      <td>{p.tipo}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatarCpf(p.cpf)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{p.telefone}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Tab>

          <Tab eventKey="compareceram" title="Compareceram">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">
                Pessoas (titulares e acompanhantes) com entrada registrada na
                portaria.
              </small>
              <Button
                size="sm"
                variant="outline-success"
                onClick={() => exportar('compareceram')}
                disabled={
                  exportando || !compareceram || compareceram.length === 0
                }
              >
                <FaFileExcel className="mr-1" /> Exportar Excel
              </Button>
            </div>
            {loadingComparecimento ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" /> Carregando...
              </div>
            ) : compareceram === null ? (
              <p className="text-muted text-center py-4">
                Clique nesta aba para carregar.
              </p>
            ) : compareceram.length === 0 ? (
              <p className="text-muted text-center py-4">
                Ninguém deu entrada ainda.
              </p>
            ) : (
              <TabelaComparecimento linhas={compareceram} mostrarEntrada />
            )}
          </Tab>

          <Tab eventKey="nao-compareceram" title="Não compareceram">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">
                Pessoas que confirmaram presença mas não deram entrada na
                portaria.
              </small>
              <Button
                size="sm"
                variant="outline-success"
                onClick={() => exportar('nao-compareceram')}
                disabled={
                  exportando || !naoCompareceram || naoCompareceram.length === 0
                }
              >
                <FaFileExcel className="mr-1" /> Exportar Excel
              </Button>
            </div>
            {loadingComparecimento ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" /> Carregando...
              </div>
            ) : naoCompareceram === null ? (
              <p className="text-muted text-center py-4">
                Clique nesta aba para carregar.
              </p>
            ) : naoCompareceram.length === 0 ? (
              <p className="text-muted text-center py-4">
                Todos os confirmados compareceram.
              </p>
            ) : (
              <TabelaComparecimento linhas={naoCompareceram} mostrarEntrada={false} />
            )}
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RelatorioEventoModal;
